import objectHash from "object-hash";
import { useEffect, useLayoutEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { debounceTime, Subject } from "rxjs";
import { selectRefreshToken } from "../../../../store/selectors/currentReportSelectors";
import {
  selectConditions,
  selectTileFields,
  selectTileSettingsState,
} from "../../../../store/selectors/fieldsStateSelectors";
import { selectDimensions } from "../../../../store/selectors/metaDataSelectors";
import { getConditionsHash } from "../../../../utilities/gridStateHelper.ts";
import { isTileConfigurationValid } from "../../../../utilities/isConfigurationValid.ts";
import type { TitleDataReturnType } from "../../../viewer/tile/hooks/tileData.types.ts";
import { getFieldsHash } from "../../common/utilities/getFieldsHash";
import useDataLoadingBuilder from "./useDataLoadingBuilder";

const useTileDataBuilder = (): TitleDataReturnType => {
  const subjectDelay = useMemo(() => new Subject<string>(), []);
  const refreshToken = useSelector(selectRefreshToken);

  const conditionsAreaValues = useSelector(selectConditions);
  const fieldsAreaValues = useSelector(selectTileFields);
  const settingsAreaValues = useSelector(selectTileSettingsState);
  const dimensions = useSelector(selectDimensions);
  const { data, isCalculating, error, calculate, failedMeasures, handleInvalidConfiguration } = useDataLoadingBuilder();

  const conditionsHash = useMemo(
    () => getConditionsHash(conditionsAreaValues, fieldsAreaValues),
    [conditionsAreaValues, fieldsAreaValues]
  );
  const fieldsHash = useMemo(() => getFieldsHash(fieldsAreaValues), [fieldsAreaValues]);

  const settingsHash = useMemo(
    () =>
      objectHash({
        compareWithPreviousPeriod: settingsAreaValues.compareWithPreviousPeriod,
        periodType: settingsAreaValues.periodType,
        showGrowth: settingsAreaValues.showGrowth,
      }),
    [settingsAreaValues]
  );

  const configurationValidity = useMemo(() => {
    return isTileConfigurationValid(conditionsAreaValues, fieldsAreaValues, dimensions);
  }, [conditionsAreaValues, dimensions, fieldsAreaValues]);

  useLayoutEffect(() => {
    const subscription = subjectDelay.pipe(debounceTime(100)).subscribe(calculate);
    return () => subscription.unsubscribe();
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, []);

  useEffect(() => {
    if (configurationValidity.valid) {
      subjectDelay.next(refreshToken);
    } else {
      handleInvalidConfiguration();
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [conditionsHash, refreshToken, settingsHash, fieldsHash, configurationValidity.valid]);

  return {
    error: error,
    configurationValidity,
    failedMeasures: failedMeasures,
    value: data?.value,
    delta: data?.delta,
    isLoading: isCalculating ?? false,
    period: data?.period,
  };
};

export default useTileDataBuilder;
